const STORAGE_KEY = 'mtag_pending_auth_action'

const TTL_MS = 30 * 60 * 1000

// Fired when an action is saved, so a page that is already settled picks it up.
export const PENDING_AUTH_ACTION_EVENT = 'mtag:pending-auth-action'

const KINDS = [
  'contactRep',
  'getPricing',
  'sendSpecs',
  'save',
  'addToCart',
  'download',
] as const

export type PendingAuthActionKind = (typeof KINDS)[number]

// What a guest tapped before being asked to sign in. Kept in sessionStorage, not
// memory, because signing up or migrating from MB2 leaves the page and comes back.
export type PendingAuthAction = {
  kind: PendingAuthActionKind
  path: string
  sku?: string
  documentId?: string
  createdAt: number
}

export type ResumeAction = Omit<PendingAuthAction, 'path' | 'createdAt'>

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string'
}

// sessionStorage is user-writable, so anything that isn't exactly this shape is
// discarded rather than resumed.
function isPendingAuthAction(value: unknown): value is PendingAuthAction {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const candidate = value as Record<string, unknown>

  if (!(KINDS as readonly unknown[]).includes(candidate.kind)) {
    return false
  }

  if (typeof candidate.path !== 'string') {
    return false
  }

  if (typeof candidate.createdAt !== 'number') {
    return false
  }

  return (
    isOptionalString(candidate.sku) && isOptionalString(candidate.documentId)
  )
}

export function savePendingAuthAction(action: ResumeAction): void {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const payload: PendingAuthAction = {
      ...action,
      path: window.location.pathname,
      createdAt: Date.now(),
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    window.dispatchEvent(new Event(PENDING_AUTH_ACTION_EVENT))
  } catch (err) {
    console.error('[pendingAuthAction] failed to save action', err)
  }
}

export function clearPendingAuthAction(): void {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage is unavailable, so there is nothing to clear.
  }
}

// The stored action for this page, if any. Anything malformed, expired or saved
// on another page is dropped.
function readPendingAuthAction(): PendingAuthAction | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)

    if (!raw) {
      return null
    }

    const parsed: unknown = JSON.parse(raw)

    if (
      !isPendingAuthAction(parsed) ||
      Date.now() - parsed.createdAt > TTL_MS
    ) {
      clearPendingAuthAction()

      return null
    }

    // Checked from another page, so the user never came back to where they
    // tapped. Drop it rather than surprise them on a later visit.
    if (parsed.path !== window.location.pathname) {
      clearPendingAuthAction()

      return null
    }

    return parsed
  } catch (err) {
    console.error('[pendingAuthAction] failed to read action', err)
    clearPendingAuthAction()

    return null
  }
}

// Whether an action of this kind is waiting on this page, without taking it.
export function hasPendingAuthAction(kind: PendingAuthActionKind): boolean {
  return readPendingAuthAction()?.kind === kind
}

// Returns the action and clears it only when it belongs to this feature, so a
// tap is resumed at most once and only where it was made.
export function takePendingAuthAction(
  kind: PendingAuthActionKind,
  sku?: string,
): PendingAuthAction | null {
  const action = readPendingAuthAction()

  if (!action || action.kind !== kind) {
    return null
  }

  if (sku !== undefined && action.sku !== sku) {
    return null
  }

  clearPendingAuthAction()

  return action
}
